import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { FormData, FormCollectionFields } from '../types';
import {
  getFieldFormatter,
  getFieldValidator,
  getFieldErrorMessage
} from '../utils/fieldFormatters';

interface FormFieldsProps {
  fields: FormCollectionFields;
  onSubmit: (data: FormData) => void;
  isLoading?: boolean;
  brandTitle?: string;
}

const FormFields: React.FC<FormFieldsProps> = ({
  fields,
  onSubmit,
  isLoading = false,
  brandTitle = 'Walky',
}) => {
  const [formattedValues, setFormattedValues] = useState<Record<string, string>>({});

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>();

  const handleFieldChange = useCallback((fieldName: string, fieldType: string, value: string) => {
    const formatter = getFieldFormatter(fieldType);
    const formattedValue = formatter(value);

    setFormattedValues(prev => ({
      ...prev,
      [fieldName]: formattedValue,
    }));

    setValue(fieldName as keyof FormData, formattedValue);
  }, [setValue]);

  const getFieldConfig = (fieldName: string, fieldType: string) => {
    // Mapper le nom du champ vers la propriété correspondante dans FormCollectionFields
    const fieldKey = `${fieldName}Field` as keyof FormCollectionFields;
    const isRequired = fields[fieldKey] === 'required';
    const validator = getFieldValidator(fieldType);
    const errorMessage = getFieldErrorMessage(fieldType);

    return {
      ...register(fieldName as keyof FormData, {
        required: isRequired ? 'Ce champ est requis' : false,
        validate: (value: string | undefined) => {
          if (!value || value.trim() === '') {
            return isRequired ? 'Ce champ est requis' : true;
          }
          return validator(value) || errorMessage;
        },
      }),
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFieldChange(fieldName, fieldType, e.target.value);
      },
      value: formattedValues[fieldName] || '',
    };
  };

  const handleFormSubmit = (data: FormData) => {
    // Clean and format data before submission
    const cleanedData: FormData = {};

    Object.entries(data).forEach(([key, value]) => {
      if (value && typeof value === 'string') {
        // Remove extra formatting for backend (e.g., spaces in phone numbers)
        if (key === 'phone') {
          // Remove spaces but preserve the + for international format
          cleanedData[key as keyof FormData] = value.replace(/\s/g, '');
        } else {
          // Trim all text fields for final submission
          cleanedData[key as keyof FormData] = value.trim();
        }
      } else {
        cleanedData[key as keyof FormData] = value;
      }
    });

    onSubmit(cleanedData);
  };

  const renderField = (
    fieldName: string,
    label: string,
    placeholder: string,
    type: string = 'text'
  ) => {
    // Mapper le nom du champ vers la propriété correspondante dans FormCollectionFields
    const fieldKey = `${fieldName}Field` as keyof FormCollectionFields;
    const fieldState = fields[fieldKey];
    if (fieldState === 'inactive') return null;

    const isRequired = fieldState === 'required';
    const fieldConfig = getFieldConfig(fieldName, type);

    return (
      <motion.div
        key={fieldName}
        className="mb-3 sm:mb-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <label
          htmlFor={fieldName}
          className="block mb-2 text-sm-responsive font-medium text-gray-900"
        >
          {label}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </label>
        <input
          {...fieldConfig}
          type={type}
          id={fieldName}
          className={`shadow-sm bg-gray-50 border text-gray-900 text-sm-responsive rounded-lg focus:ring-yellow-500 focus:border-yellow-500 outline-none block w-full p-2.5 sm:p-3 transition-colors touch-target no-zoom ${
            errors[fieldName as keyof FormData]
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300'
          }`}
          placeholder={`${placeholder} ${isRequired ? '(requis)' : '(facultatif)'}`}
        />
        {errors[fieldName as keyof FormData] && (
          <p className="mt-1 text-xs-responsive text-red-600">
            {errors[fieldName as keyof FormData]?.message || 'Ce champ est requis'}
          </p>
        )}
      </motion.div>
    );
  };

  return (
    <motion.div
      className="center-safe w-full relative min-h-screen-safe landscape-compact ultra-compact"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="absolute inset-0 z-0">
        <img
          src="/src/utils/background.png"
          className="w-full h-full object-cover"
          alt="background"
        />
      </div>

      <motion.form
        className="w-[95%] sm:w-[90%] max-w-md mx-auto z-10 bg-white mobile-padding p-6 sm:p-8 rounded-xl shadow-2xl my-4 sm:my-8"
        onSubmit={handleSubmit(handleFormSubmit)}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-[Agency] text-gray-900 mb-2 responsive-text">
            {brandTitle}
          </h1>
          <p className="text-sm-responsive text-gray-600 font-[DMSans] px-2">
            Veuillez entrer vos informations pour continuer
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          {renderField('companyName', 'Société', 'Nom de votre société', 'companyName')}
          {renderField('firstname', 'Prénom', 'Votre prénom', 'firstname')}
          {renderField('lastname', 'Nom', 'Votre nom', 'lastname')}
          {renderField('email', 'Email', 'Votre adresse email', 'email')}
          {renderField('phone', 'Téléphone', 'Votre numéro de téléphone', 'tel')}
          {renderField('birthdate', 'Date de naissance', 'JJ/MM/AAAA', 'date')}
        </div>

        <div className="mt-6 sm:mt-8 flex justify-center">
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-3 px-6 rounded-lg font-medium text-white transition-all duration-200 text-sm-responsive touch-target ${
              isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-yellow-400 hover:bg-yellow-500 focus:ring-4 focus:ring-yellow-300 transform hover:scale-105'
            }`}
          >
            {isLoading ? 'Chargement...' : 'Obtenir ma carte'}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
};

export default FormFields;
